import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api, apiClient } from './api';
import type { DocumentListItem } from '../types';

/**
 * Query keys for documents
 */
export const documentKeys = {
  all: ['documents'] as const,
  byContract: (contractId: string) => [...documentKeys.all, 'contract', contractId] as const,
};

/**
 * Fetch documents for a specific contract
 */
const fetchContractDocuments = async (contractId: string): Promise<DocumentListItem[]> => {
  return api.get<DocumentListItem[]>(`/documents/by-contract/${contractId}`);
};

/**
 * Upload a document.
 *
 * Goes through apiClient so the bearer token and the 401 redirect apply. The multipart
 * content type overrides the client's JSON default (axios would otherwise serialize the
 * FormData as JSON); axios then lets the browser set the boundary. No timeout: files can
 * be up to 50 MB and the client default of 30 s is too short for slow links.
 */
const uploadDocument = async (formData: FormData): Promise<DocumentListItem> => {
  try {
    const response = await apiClient.post<DocumentListItem>('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
    return response.data;
  } catch (err) {
    const serverMessage = axios.isAxiosError(err)
      ? (err.response?.data as { error?: string } | undefined)?.error
      : undefined;
    throw new Error(serverMessage || 'Upload failed');
  }
};

/**
 * Delete a document
 */
const deleteDocument = async (id: string): Promise<void> => {
  return api.delete<void>(`/documents/${id}`);
};

/**
 * Download a document and hand it to the browser as a file.
 *
 * Goes through apiClient so the bearer token and the 401 redirect apply. No timeout for the
 * same reason as uploads.
 */
export const downloadDocument = async (id: string, fileName: string): Promise<void> => {
  let blob: Blob;
  try {
    const response = await apiClient.get<Blob>(`/documents/${id}/download`, {
      responseType: 'blob',
      timeout: 0,
    });
    blob = response.data;
  } catch {
    throw new Error('Download failed');
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Hook: Fetch documents for a contract
 */
export const useContractDocuments = (contractId: string | undefined) => {
  return useQuery({
    queryKey: documentKeys.byContract(contractId!),
    queryFn: () => fetchContractDocuments(contractId!),
    enabled: !!contractId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook: Upload document mutation
 */
export const useUploadDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadDocument,
    onSuccess: (_, variables) => {
      // Get contractId from FormData
      const contractId = variables.get('contractId') as string;
      if (contractId) {
        queryClient.invalidateQueries({ queryKey: documentKeys.byContract(contractId) });
      }
      // Also invalidate contract queries to update document count
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });
};

/**
 * Hook: Delete document mutation
 */
export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      // Invalidate all document queries
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      // Also invalidate contract queries to update document count
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};
